// -----------------------------------------------------------------------------
// AddCandidatureForm.jsx – UI harmonisée avec AddAppelOffreForm + InfoAlert
// -----------------------------------------------------------------------------
import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  Paper,
  TextField,
  styled,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon, Info as InfoIcon } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { createCandidatures } from "../../redux/candidatureSlice";
import * as Yup from "yup";

/* ────────────────────────────────────────────────────────────────────────── */
/*  🎨   DESIGN TOKENS – mêmes que AddAppelOffreForm                        */
/* ────────────────────────────────────────────────────────────────────────── */
const NAVY       = "#2F4E8B";
const NAVY_LIGHT = "#4a6bad";
const LINE_GREY  = "#C6CDD6";
const LABEL_GREY = "#4F5565";
const OUTLINE    = "#D4D9E0";

/* ───  GradientPaper identique à AddAppelOffreForm                            */
const GradientPaper = ({ children, sx }) => (
  <Paper
    elevation={8}
    sx={{
      borderRadius: 3,
      p: { xs: 3, md: 4 },
      width: { xs: "100%", md: 600 },
      mx: "auto",
      background:
        "linear-gradient(135deg, #FFFFFF 0%, #F6F9FC 60%, #E9F0F9 100%)",
      position: "relative",
      ...sx,
    }}
  >
    {children}
  </Paper>
);

/* ───  CustomTextField arrondi & transitions douces                            */
const CustomTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    borderRadius: 8,
    transition: "all .25s ease",
    "& fieldset": { borderColor: OUTLINE },
    "&:hover fieldset": { borderColor: NAVY_LIGHT },
    "&.Mui-focused fieldset": { borderColor: NAVY_LIGHT },
  },
  "& .MuiInputBase-input": { fontSize: 14 },
});

/* ───  InfoAlert personnalisé pour message info                               */
const InfoAlert = styled(Alert)({
  borderRadius: 8,
  backgroundColor: "#E9F0F9",
  color: NAVY,
  border: `1px solid ${OUTLINE}`,
  "& .MuiAlert-icon": {
    color: NAVY_LIGHT,
  },
});

const AddCandidatureForm = ({ appelOffreId, handleClose }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({ commentaire: "" });
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  const validationSchema = Yup.object().shape({
    commentaire: Yup.string()
      .max(200, "Le commentaire ne peut pas dépasser 200 caractères.")
      .required("Le commentaire est requis."),
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      await dispatch(
        createCandidatures({
          appelOffreId,
          commentaire: formData.commentaire,
        })
      ).unwrap();
      setSuccessMessage("Candidature ajoutée avec succès !");
      setTimeout(() => {
        setSuccessMessage("");
        handleClose();
      }, 2000);
    } catch (err) {
      if (err.name === "ValidationError") {
        setError(err.errors.join(" "));
      } else {
        setError("Une erreur est survenue.");
      }
    }
  };

  const handleUpdateProfile = () => {
    window.dispatchEvent(
      new CustomEvent("navigateToComponent", {
        detail: { component: "profilCandidature" },
      })
    );
    handleClose();
  };

  return (
    <GradientPaper>
      {/* Close button */}
      <IconButton
        onClick={handleClose}
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          color: LABEL_GREY,
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Title */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          color: NAVY,
          mb: 3,
          textAlign: "center",
        }}
      >
        Ajouter une candidature
      </Typography>

      {/* Alerts */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      <InfoAlert
        severity="info"
        icon={<InfoIcon fontSize="inherit" />}
        sx={{ mb: 2 }}
      >
        Veuillez mettre à jour votre profil de candidature avant de postuler.
      </InfoAlert>

      {/* Update profile button */}
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Button
          variant="contained"
          onClick={handleUpdateProfile}
          sx={{
            backgroundColor: NAVY,
            color: "#fff",
            fontWeight: "bold",
            textTransform: "none",
            px: 4,
            borderRadius: 1,
            "&:hover": { backgroundColor: NAVY_LIGHT },
          }}
        >
          Mettre à jour votre profil
        </Button>
      </Box>

      {/* Form */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        <CustomTextField
          label="Commentaire"
          name="commentaire"
          value={formData.commentaire}
          onChange={handleChange}
          multiline
          rows={4}
          fullWidth
          helperText={`${formData.commentaire.length}/200`}
          error={!!error}
        />

        {/* Buttons */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              borderColor: OUTLINE,
              color: LABEL_GREY,
              textTransform: "none",
              px: 4,
              fontWeight: "bold",
              borderRadius: 1,
              "&:hover": { backgroundColor: OUTLINE, color: NAVY },
            }}
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: NAVY,
              color: "#fff",
              textTransform: "none",
              px: 5,
              fontWeight: "bold",
              borderRadius: 1,
              "&:hover": { backgroundColor: NAVY_LIGHT },
              height: 42,
            }}
          >
            Ajouter
          </Button>
        </Box>
      </Box>
    </GradientPaper>
  );
};

export default AddCandidatureForm;
